import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { LoadingButton } from '@/components/LoadingButton';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { usePostSchedules, getGetSchedulesQueryKey } from '@/api/control/control-plane/control-plane';
import type { ScheduleConfigRequest, DayOfWeek } from '@/api/control/missionImpactAPI.schemas';
import { useLanguage } from '@/contexts/LanguageContext';

type ScheduleType = ScheduleConfigRequest['$type'];

interface CreateScheduleDialogProps {
  orgId: string;
  profileId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Dialog component for creating new quiet time schedules
 */
export function CreateScheduleDialog({
  orgId,
  profileId,
  open,
  onOpenChange,
}: CreateScheduleDialogProps) {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [scheduleType, setScheduleType] = useState<ScheduleType>('QuietTimeDaily');
  const [startHour, setStartHour] = useState('22');
  const [startMinute, setStartMinute] = useState('00');
  const [endHour, setEndHour] = useState('06');
  const [endMinute, setEndMinute] = useState('00');
  const [selectedDays, setSelectedDays] = useState<DayOfWeek[]>([5, 6, 0]); // Friday, Saturday, Sunday

  const daysOfWeek = [
    t.profileDetail.daysOfWeek.sunday,
    t.profileDetail.daysOfWeek.monday,
    t.profileDetail.daysOfWeek.tuesday,
    t.profileDetail.daysOfWeek.wednesday,
    t.profileDetail.daysOfWeek.thursday,
    t.profileDetail.daysOfWeek.friday,
    t.profileDetail.daysOfWeek.saturday,
  ];

  const calculateDurationInHours = (start: string, end: string): number => {
    const startMinutes = parseInt(start.split(':')[0]) * 60 + parseInt(start.split(':')[1] || '0');
    const endMinutes = parseInt(end.split(':')[0]) * 60 + parseInt(end.split(':')[1] || '0');

    let durationMinutes = endMinutes - startMinutes;
    if (durationMinutes <= 0) {
      durationMinutes += 24 * 60;
    }

    return durationMinutes / 60;
  };

  const startTimeStr = `${startHour.padStart(2, '0')}:${startMinute.padStart(2, '0')}`;
  const endTimeStr = `${endHour.padStart(2, '0')}:${endMinute.padStart(2, '0')}`;
  const duration = calculateDurationInHours(startTimeStr, endTimeStr);
  const isValidDuration = duration > 0 && duration <= 8;

  const createScheduleMutation = usePostSchedules({
    mutation: {
      onSuccess: () => {
        toast.success(t.profileDetail.createScheduleDialog.createSuccess);
        void queryClient.invalidateQueries({ queryKey: getGetSchedulesQueryKey(orgId, profileId) });
        handleOpenChange(false);
      },
      onError: () => {
        toast.error(t.profileDetail.createScheduleDialog.createError);
      },
    },
  });

  const handleConfirm = () => {
    const config =
      scheduleType === 'QuietTimeDaily'
        ? {
            $type: 'QuietTimeDaily' as const,
            startTime: `${startHour.padStart(2, '0')}:${startMinute.padStart(2, '0')}:00`,
            endTime: `${endHour.padStart(2, '0')}:${endMinute.padStart(2, '0')}:00`,
          }
        : {
            $type: 'QuietTimeWeekly' as const,
            days: selectedDays,
            startTime: `${startHour.padStart(2, '0')}:${startMinute.padStart(2, '0')}:00`,
            endTime: `${endHour.padStart(2, '0')}:${endMinute.padStart(2, '0')}:00`,
          };

    createScheduleMutation.mutate({
      orgId,
      profileId,
      data: { config },
    });
  };

  const resetForm = () => {
    setScheduleType('QuietTimeDaily');
    setStartHour('22');
    setStartMinute('00');
    setEndHour('06');
    setEndMinute('00');
    setSelectedDays([5, 6, 0]);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      resetForm();
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t.profileDetail.createScheduleDialog.title}</DialogTitle>
          <DialogDescription>
            {t.profileDetail.createScheduleDialog.description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="schedule-type">{t.profileDetail.createScheduleDialog.type}</Label>
            <Select value={scheduleType} onValueChange={(value) => { setScheduleType(value as ScheduleType); }}>
              <SelectTrigger id="schedule-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="QuietTimeDaily">{t.profileDetail.createScheduleDialog.everyDay}</SelectItem>
                <SelectItem value="QuietTimeWeekly">{t.profileDetail.createScheduleDialog.specificDays}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {scheduleType === 'QuietTimeWeekly' && (
            <div className="space-y-2">
              <Label>{t.profileDetail.createScheduleDialog.selectDays}</Label>
              <div className="grid grid-cols-2 gap-2">
                {daysOfWeek.map((day, i) => {
                  const index = i as DayOfWeek;
                  return (
                  <div key={day} className="flex items-center space-x-2">
                    <Checkbox
                      id={`day-${String(index)}`}
                      checked={selectedDays.includes(index)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedDays([...selectedDays, index].sort());
                        } else {
                          setSelectedDays(selectedDays.filter((d) => d !== index));
                        }
                      }}
                    />
                    <label
                      htmlFor={`day-${String(index)}`}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {day}
                    </label>
                  </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t.profileDetail.createScheduleDialog.startTime}</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="0"
                  max="23"
                  value={startHour}
                  onChange={(e) => { setStartHour(e.target.value); }}
                  className="w-20"
                  placeholder={t.profileDetail.createScheduleDialog.hourPlaceholder}
                />
                <span>:</span>
                <Input
                  type="number"
                  min="0"
                  max="59"
                  value={startMinute}
                  onChange={(e) => { setStartMinute(e.target.value); }}
                  className="w-20"
                  placeholder={t.profileDetail.createScheduleDialog.minutePlaceholder}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t.profileDetail.createScheduleDialog.endTime}</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="0"
                  max="23"
                  value={endHour}
                  onChange={(e) => { setEndHour(e.target.value); }}
                  className="w-20"
                  placeholder={t.profileDetail.createScheduleDialog.hourPlaceholder}
                />
                <span>:</span>
                <Input
                  type="number"
                  min="0"
                  max="59"
                  value={endMinute}
                  onChange={(e) => { setEndMinute(e.target.value); }}
                  className="w-20"
                  placeholder={t.profileDetail.createScheduleDialog.minutePlaceholder}
                />
              </div>
            </div>
          </div>

          <div className="min-h-[60px]">
            {!isValidDuration && (
              <Alert className="border-destructive/50 text-destructive dark:border-destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {t.profileDetail.createScheduleDialog.durationError}
                </AlertDescription>
              </Alert>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => { handleOpenChange(false); }}
            disabled={createScheduleMutation.isPending}
          >
            {t.profileDetail.createScheduleDialog.cancel}
          </Button>
          <LoadingButton onClick={handleConfirm} disabled={!isValidDuration} isLoading={createScheduleMutation.isPending}>
            <Plus className="h-4 w-4" />
            {t.profileDetail.createScheduleDialog.create}
          </LoadingButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
